(ns app.front.view.group.admin.import.post-processing
  (:require [app.domain.business.model :as business-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.import.session :as group.admin.import.session]
            [app.front.view.group.admin.import.session-list :as import-list]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(def import-types-with-businesses
  #{:import-session.type/contractor-registration
    :import-session.type/licence})

(rf/reg-sub ::sessions-for-processing
  (fn [[_ group-id]]
    (rf/subscribe [::import-list/sessions group-id]))
  (fn [sessions _]
    (->> sessions
         (filter (du/in-state-fn :import-session.state/done))
         (filter #(->> % :import-session/type (contains? import-types-with-businesses))))))

(rf/reg-sub ::has-applicable-sessions?
  (fn [[_ group-id]]
    (rf/subscribe [::sessions-for-processing group-id]))
  :-> (complement empty?))

(rf/reg-event-fx ::fetch-session-actions
  (fn [{:keys [db]} [_ authority-group-id session-id]]
    {:comm/command {:command    :import/post-processing-actions
                    :method     :get
                    :data       (util/mmap authority-group-id session-id)
                    :on-success [::fetch-session-actions.success session-id]}
     :db           (-> db
                       (update-in [::state :loading] (fnil #(conj % session-id) #{})))}))

(rf/reg-event-db ::fetch-session-actions.success
  (fn [db [_ session-id _ {:keys [session actions performed-actions]}]]
    (-> db
        (update-in [::state :loading] disj session-id)
        (assoc-in [::state :sessions session-id] session)
        (assoc-in [::state :performed-actions session-id] performed-actions)
        (assoc-in [::state :actions session-id] actions))))

(rf/reg-event-fx ::perform
  (fn [{:keys [db]} [_ authority-group-id session-id action-name]]
    {:comm/command {:command    :import/run-post-processing-action
                    :method     :post
                    :data       (util/mmap authority-group-id session-id action-name)
                    :notify     {:success :data-import/post-processing-started}
                    :on-success [::perform.success session-id]}
     :db           (update-in db [::state :actions session-id] #(remove (comp (partial = action-name) :name) %))}))

(rf/reg-event-db ::perform.success
  (fn [db [_ session-id status {:keys [performed-action]}]]
    (if (= status 200)
      (update-in db [::state :performed-actions session-id] #(conj (or % []) performed-action))
      db)))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::loading?
  :<- [::state]
  (fn [{:keys [loading]} [_ session-id]]
    (contains? loading session-id)))

(rf/reg-sub ::performed-actions
  :<- [::state]
  (fn [{:keys [performed-actions]} [_ session-id]]
    (seq (get performed-actions session-id))))

(rf/reg-sub ::available-actions
  :<- [::state]
  (fn [{:keys [actions]} [_ session-id]]
    (get actions session-id)))

(rf/reg-sub ::session
  :<- [::state]
  (fn [{:keys [sessions]} [_ session-id]]
    (get sessions session-id)))

(defmulti action-details (fn [{:keys [action]}] (:name action)))

(def max-businesses 9)

(defn- business-list [{:keys [lang]} businesses]
  (for-frag [business businesses]
    [:div.flex.gap-1.items-baseline.flex-wrap
     (cc/link
       {:href (href/href :view/business {:business-id (:db/id business)})}
       (business-model/business-name business lang))
     [:span.font-mono (:business/email business)]]))

(defmethod action-details :data-import.post-processing-action/invite-business-owners
  [{:keys [action lang tr]}]
  (r/with-let [*show-all? (r/atom false)]
    [:<>
     [:div.mt-4.grid.grid-cols-3.gap-2.mb-4
      [business-list (util/mmap lang) (cond->> (:businesses action)
                                        (not @*show-all?) (take max-businesses))]]
     (when (and (not @*show-all?)
                (> (count (:businesses action)) max-businesses))
       [ui/show-more-button {:on-click #(swap! *show-all? not)} (tr [:show-more])])]))

(defn processing-view [{{{:keys [session-id group-id]} :path} :parameters :as match}]
  (let [tr      (<sub [::gas/group-tr])
        tz      (<sub [::gas/group-tz])
        lang    (<sub [:language])
        session (<sub [::session session-id])]
    [acommon/admin-page-v2
     (util/mmap {:title (tr [:data-import/post-processing])} match)
     [:div
      (if (<sub [::loading? session-id])
        [section/section-skeleton]
        [acommon/section
         {:title (tr [:data-import/post-processing])}
         [section/subsection-3
          {:class "mb-4"
           :title (tr [:data-import/session-info])}
          [:div.gap-2.flex.flex-col
           [:div (tr [:import-session/type]) ": " (tr [(:import-session/type session)])]
           [:div (tr [:created-by] [(date-util/date-time-in-tz (:entity/created session) tz)
                                    (-> session :import-session/user user-model/full-name)])]
           [:div (tr [:data-import/completed] [(date-util/date-time-in-tz (:completed session) tz)])]]]
         (when-let [past-actions (<sub [::performed-actions session-id])]
           [section/subsection-2
            (util/mmap {:title                (tr [:data-import/performed-actions])
                        :initially-collapsed? true}
                       tr)
            (for-frag [{:keys [:import-session-action-log/action
                               :entity/created
                               :entity/creator]} past-actions]
              [:div.flex.gap-2
               [:span.font-bold (tr [action])]
               [:span (tr [:last-change-by]
                          [(date-util/date-time-in-tz created tz)
                           (user-model/full-name creator)])]])])
         (when (-> (<sub [::available-actions session-id]) empty?)
           [:div.text-ui-night-sky.mt-8.text-lg (tr [:data-import/post-processing-no-actions])])
         (for-frag [action (<sub [::available-actions session-id])]
           (let [action-name    (:name action)
                 action-name-tr (tr [action-name])]
             [section/subsection
              {:title action-name-tr}
              [ui/help-2 {:color :white} (tr [(keyword (str (namespace action-name) "-desc") (name action-name))])]
              [action-details (util/mmap tr lang action)]
              [:div.flex.justify-end
               [ui/modal-confirmation-button
                (util/mmap {:id                  (str "perform-" (name action-name))
                            :on-yes-click        #(>evt [::perform group-id session-id action-name])
                            :trigger-button-text (tr [:data-import/perform-post-process-action])
                            :trigger-button-opts {:kind :danger}}
                           tr)
                [:div (tr [:data-import/perform-post-process-action-confirm] [action-name-tr])]]]]))])]]))

(defn post-processing-breadcrumb [{:keys [match tr]}]
  (let [params (-> match :parameters :path)]
    [:view/group.admin.import.post-processing
     params
     (tr [:data-import/post-processing])]))

(def route
  {:name        :view/group.admin.import.post-processing
   :view        processing-view
   :own-layout? true
   :breadcrumb  {:items [group.admin.import.session/session-breadcrumb
                         post-processing-breadcrumb]}
   :parameters  {:path {:group-id   s/Int
                        :session-id s/Int}}
   :controllers [{:parameters {:path [:group-id :session-id]}
                  :start      (fn [{{:keys [group-id session-id]} :path}]
                                (>evt [::fetch-session-actions group-id session-id]))}]})

(defn session->href [session]
  (href/href :view/group.admin.import.post-processing
             {:group-id   (-> session :domain/authority-group :db/id)
              :session-id (:db/id session)}))

(defn post-processing-section [{:keys [tr group-id]}]
  (when (<sub [::has-applicable-sessions? group-id])
    [:section
     [:h3 (tr [:data-import/post-processing])]
     [ui-table/modern-responsive-table
      (util/mmap {:items      (<sub [::sessions-for-processing group-id])
                  :link       {:make-href   session->href
                               :description (tr [:data-import/post-processing])}
                  :get-status (comp import-list/state->status :domain/state)
                  :cols       [[:file/filename
                                (comp :file/filename :import-session/file)]
                               [:import-session/type
                                #(tr [(:import-session/type %)])]
                               [:domain/state (comp tr vector :domain/state)]
                               [:entity/created
                                (comp (<sub [::date-util/date-fn]) :entity/created)]]}
                 tr)]]))
